import { Component, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { ModalService } from '@app/shared/modal.service';
import { AuthenticationService, I18nService, Logger } from '@app/core';
import { Subscription } from 'rxjs/Subscription';
import { Router } from '@angular/router';
import { PusherService } from '@app/core/pusher.service';
import { NotificationsService } from '@app/core/notifications.service';
import { IntercomService } from '@app/core/intercom.service';
import * as amplitude from 'amplitude-js';
import { JackpotService } from '@app/core/jackpot.service';
import { dropdownAnimation } from '@app/shared/animations/dropdown.animations';
import { WagerService } from '@app/core/wager.service';
import { BonusService } from '@app/core/bonus.service';
import { AppService } from '@app/core/app.service';
import { DepositService } from '@app/core/account/deposit.service';
import { WithdrawService } from '@app/core/account/withdraw.service';
import { BalanceService, currencyList, CurrencyType } from '@app/core/balance.service';
import { ServerType } from '@app/games/models/server-type.model';
import { environment } from '@env/environment';
import * as moment from 'moment';

const logger = new Logger('@app:header-navigation.component.ts');

@Component({
  selector: 'app-header-navigation',
  templateUrl: './header-navigation.template.html',
  styleUrls: ['./header-navigation.template.scss'],
  styles: [
    `
      .currency {
        display: inline-block;
        padding: 15px;
        cursor: pointer;
      }
    `,
  ],
  /*host: {
    '(document:click)': 'onClickOutside($event)',
  },*/
  animations: [dropdownAnimation],
})
export class HeaderNavigationComponent implements OnInit, OnChanges, OnDestroy {
  accountDropdown: boolean = false;
  notificationsDropdown: boolean = false;
  balanceDropdown: boolean = false;

  notificationPulse: boolean = false;

  notifications: any = [];
  notificationsCounter = 0;
  notificationsBonusCounter = 0;

  pushedUser: string = null;

  userInfo$: Subscription = null;
  jackpotInfo$: Subscription = null;
  chatVisibility$: Subscription = null;

  tabBonusesId: string = 'bonus';
  tabs: any[] = []; // Account tabs

  showJackpot: boolean = false;
  showWagerProgress: boolean = false;

  $count: Subscription;

  public currencyList = currencyList;

  public build: string = environment.build !== 'production' ? environment.build : null;
  public santaHat: boolean = moment().isBetween('2020-12-01', '2021-01-12', null, '[]');

  constructor(
    public intercom: IntercomService,
    public modalService: ModalService,
    public auth: AuthenticationService,
    public i18n: I18nService,
    public router: Router,
    public balance: BalanceService,
    private pusher: PusherService,
    private notificationsService: NotificationsService,
    private renderer: Renderer2,
    private jackpotService: JackpotService,
    private wagerService: WagerService,
    private bonusService: BonusService,
    private appService: AppService,
    private withdrawService: WithdrawService,
    private depositService: DepositService,
  ) {
    this.tabs = [
      {
        id: 'bonus',
        url: '/account/bonus',
        icon: 'fa-gift',
        title: 'Bonuses',
        notificationCount: 0,
        desktopDropdown: false,
        mobileDropdown: true,
      },
      {
        id: 'deposit',
        url: ['/', { outlets: { modal: 'deposit' } }],
        icon: 'fa-dollar-sign',
        title: 'Deposit',
        notificationCount: 0,
        desktopDropdown: false,
        mobileDropdown: false,
        balanceDropdown: true,
      },
      {
        id: 'withdrawal',
        url: ['/', { outlets: { modal: 'withdraw' } }],
        icon: 'fa-usd-circle',
        title: 'Withdrawal',
        notificationCount: 0,
        desktopDropdown: false,
        mobileDropdown: false,
        balanceDropdown: true,
      },
      {
        id: 'games-history',
        url: '/account/games-history',
        icon: 'fa-history',
        title: 'Games History',
        notificationCount: 0,
        desktopDropdown: true,
        mobileDropdown: true,
      },
      {
        id: 'account-settings',
        url: '/account/password',
        icon: 'fa-sliders-h',
        title: 'Account settings',
        desktopDropdown: true,
        mobileDropdown: true,
      },
      {
        id: 'transaction-history',
        url: '/account/transaction-history',
        icon: 'fa-history',
        title: 'Transaction History',
        notificationCount: 0,
        desktopDropdown: false,
        mobileDropdown: false,
        balanceDropdown: true,
      },
    ];
  }

  ngOnInit() {
    this.userInfo$ = this.auth.userInfo$.subscribe(
      (user: any) => {
        if (user && user._id && user._id !== this.pushedUser) {
          this.pushedUser = user._id;
          this.getNotifications();
          this.listenForNotifications(user._id);
          this.bonusCount();
        }
      },
      () => {}
    );

    this.jackpotService.jackpotInfo$.subscribe((data: any) => {
      this.showJackpot = !!data;
    });

    this.wagerService.wagerData$.subscribe((wagerData: any) => {
      logger.info('header navigation wager data: ' + JSON.stringify(wagerData));
      this.showWagerProgress = !!wagerData.target;
    });

    logger.info('init');
    this.listenForGlobal();
  }

  ngOnChanges(changes: SimpleChanges) {}

  ngOnDestroy() {
    if (this.userInfo$) {
      this.userInfo$.unsubscribe();
    }
    if (this.jackpotInfo$) {
      this.jackpotInfo$.unsubscribe();
    }
    if (this.chatVisibility$) {
      this.chatVisibility$.unsubscribe();
    }
    if (this.auth.userInfo && this.auth.userInfo._id) {
      this.pusher.unsubscribe(this.auth.userInfo._id);
    }
    if (this.$count) {
      this.$count.unsubscribe();
    }
    this.pusher.unsubscribe('global');
  }

  toggleAccountDropdown($event: Event): void {
    this.setAccountDropdown(!this.accountDropdown, $event);
  }

  setAccountDropdown(value: boolean, $event: Event): void {
    $event.preventDefault();
    this.accountDropdown = value;
    if (this.accountDropdown) {
      this.renderer.addClass(document.getElementsByTagName('html')[0], 'mobile-overlay-open');
    } else {
      this.renderer.removeClass(document.getElementsByTagName('html')[0], 'mobile-overlay-open');
    }
  }

  listenForNotifications(userId: string) {
    logger.debug('listen for notifications & balance', userId);
    this.pusher.connect(this.auth.credentials.accessToken);

    const channel = this.pusher.private(userId);

    channel.bind('update.balance', (data: any) => {
      if (this.auth.isMainServer()) {
        logger.debug('update balance: ' + data.balance);
        // this.auth.balance = data.balance;
        this.auth.realEdgBalance = data.realEdgBalance;
        this.auth.bonusEdgBalance = data.bonusEdgBalance;
        this.auth.convertedBalance = data.convertedBalance;
        this.auth.convertedCurrency = data.convertedCurrency;
      }
      this.auth
        .getUserInfo()
        .toPromise()
        .catch(() => {});
    });

    channel.bind('wager.progress', (data: any) => {
      this.wagerService.setWagerData(data);
    });

    channel.bind('command', (event: any) => {
      if (event.command === 'logout') {
        this.auth.logout().subscribe();
      }
    });

    channel.bind('notification', (event: any) => {
      event.text = this.notificationsService.textBuilder(event);
      this.notifications.unshift(event);
      this.notificationsCounter = this.notifications.filter((item: any) => item.seenAt === null).length;
      this.notificationPulse = true;
      this.notificationsService.updateInfo(event.event);
      if (event.event === 'reward.received' && event.data.collectibleSlug === 'jackpot-chest') {
        if (!localStorage.getItem('chest-for-deposit') || parseInt(localStorage.getItem('chest-for-deposit'), 10) < 1) {
          this.modalService.openChestForDeposit();
          localStorage.setItem(
            'chest-for-deposit',
            localStorage.getItem('chest-for-deposit')
              ? (parseInt(localStorage.getItem('chest-for-deposit'), 10) + 1).toString()
              : '1'
          );
        }
      }
      if (['deposit.credit', 'deposit.first'].includes(event.event)) {
        this.depositService.depositSubject.next(event);
      }
      if (['withdraw.success', 'withdraw.aborted', 'withdraw.failed'].includes(event.event)) {
        this.withdrawService.withdrawRequestedSubject.next(true);
      }
    });
  }

  listenForGlobal() {
    if (!this.pusher.isConnected('public')) {
      this.pusher.connect();
    }
    const channel = this.pusher.subscribe('global', 'public');
    channel.bind('command', (event: any) => {
      switch (event.command) {
        case 'logout':
          this.auth.logout().subscribe();
          break;
        case 'jackpot.chest-opened':
        case 'jackpot.client-seed':
        case 'jackpot.winner':
          this.jackpotService.getAuthInfo();
          this.jackpotService.getInfo();
          break;
      }
    });
  }

  getNotifications() {
    this.notificationsService.get().then(
      (data: any) => {
        this.notifications = data
          .map(item => {
            item.text = this.notificationsService.textBuilder(item);
            return item;
          })
          .filter(item => {
            return item.text !== null;
          });
        this.notificationsCounter = this.notifications.filter((item: any) => item.seenAt === null).length;
        logger.debug(data);
      },
      (error: any) => {
        logger.error(error);
      }
    );
  }

  notificationMark(id: string) {
    if (this.notifications.filter((item: any) => item._id === id)[0].seenAt) { return; }
    this.notificationsService.markAsRead(id).then(
      (data: any) => {
        this.notifications.filter((item: any) => item._id === id)[0].seenAt = new Date();
        this.notificationsCounter = this.notifications.filter((item: any) => item.seenAt === null).length;
      },
      (error: any) => {
        logger.debug(error);
      }
    );
  }

  showNotifications() {
    this.router.navigate(['/', { outlets: { modal: 'notifications' } }]);
  }

  openJackpot() {
    this.router.navigate(['/jackpot']);
  }

  trackSignUpOpen() {
    amplitude.getInstance().logEvent('Onboarding - Sign up');
  }

  trackDepositOpen() {
    amplitude.getInstance().logEvent('Deposit - Open', {
      Success: true,
    });
  }

  trackAccountTabOpen(id: string) {
    if (id === 'account') {
      amplitude.getInstance().logEvent('Account - Open');
    }

    if (id === 'fairness') {
      amplitude.getInstance().logEvent('Account - Fairness');
    }

    if (id === 'withdrawal') {
      amplitude.getInstance().logEvent('Withdrawal - Open');
    }
  }

  trackOpenSupport() {
    logger.info('Tracking support open event');
    amplitude.getInstance().logEvent('Support - Open');
  }

  bonusCount() {
    if (this.auth.credentials && this.auth.credentials.accessToken) {
      if (this.$count) {
        this.$count.unsubscribe();
      }
      this.$count = this.bonusService.getInactiveBonuses(this.auth.credentials.accessToken).subscribe((data: any) => {
        this.tabs.forEach(tab => {
          if (tab.id === this.tabBonusesId) {
            tab.notificationCount = data;
            this.notificationsBonusCounter = data;
          }
        });
      });
    }
    setTimeout(() => {
      this.bonusCount();
    }, 35000);
  }

  public allowChat(): boolean {
    return this.appService.getFlag('allowChat');
  }

  public changeCurrency(currency: CurrencyType): void {
    const current = this.balance.getCurrency();
    if (current === currency) {
      return;
    }

    if (!this.router.url.split('/').includes('games')) {
      this.setCurrency(currency);
      return;
    }

    this.modalService
      .openAlert({
        title: 'Warning',
        text: `Switching currency will cancel games started on ${current.toUpperCase()}. Do you want to continue?`,
        ok: `Switch to ${currency.toUpperCase()}`,
        cancel: `Continue with ${current.toUpperCase()}`,
      })
      .result.then(() => {
        this.setCurrency(currency);
      })
      .catch(() => {});
  }

  public setCurrency(currency: CurrencyType): void {
    this.balance.setCurrency(currency);
    if (!this.auth.isMainServer()) {
      this.auth.setMode(ServerType.Main);
    }
    this.auth.getUserInfo().subscribe(_ => {});
    amplitude.getInstance().logEvent('Currency - Switched', {
      Currency: currency.toString().toUpperCase(),
    });
  }

  logout() {
    amplitude.getInstance().logEvent('Account - Log Out');

    this.auth.logout();
    this.router.navigate(['/home', { outlets: { modal: null } }]);
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/_v2/template/header-navigation/header-navigation.component.ts